// Agendamiento · mover una reserva a otra hora (o darle hora a una pagada sin hora).
//
// Muestra las horas libres del día elegido para el mismo servicio, en todos los
// profesionales y boxes que lo atienden. El panel no tiene las reglas de
// anticipación del sitio público: puede agendar para hoy mismo. Que la hora siga
// libre lo garantiza el backend al confirmar; si alguien la tomó entre medio, se
// avisa y se recarga.

import React, { useCallback, useEffect, useState } from 'react';
import { Alert, DatePicker, Modal, Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import dayjs from 'dayjs';
import Agenda from '../../../../service/Agenda';
import { mensajeDeError } from '../../../../service/client';
import { useNotify } from '../../../../providers/NotificationProvider';
import { claseBtnPrimario, claseBtnSecundario } from '../../../Ui/Admin/PiezasAdmin';
import { Vacio } from './piezas';
import { fechaHora, fechaLarga } from './util';

const claseFlecha =
  'w-9 h-9 inline-flex items-center justify-center rounded-full border border-gray-200 text-gray-600 hover:border-primary hover:text-primary transition-colors disabled:opacity-40 disabled:pointer-events-none';

export default function ReprogramarModal({ cita, open, onClose, onListo }) {
  const notify = useNotify();
  const [dia, setDia] = useState(dayjs());
  const [horas, setHoras] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [elegida, setElegida] = useState(null); // { inicio, hora, agendaId, agendaNombre }
  const [guardando, setGuardando] = useState(false);

  // Parte en el día de la reserva (si es futuro): lo normal es moverla dentro de la misma semana.
  useEffect(() => {
    if (!open) return;
    const inicio = cita?.inicio ? dayjs(cita.inicio) : null;
    setDia(inicio && inicio.isAfter(dayjs(), 'day') ? inicio : dayjs());
  }, [open, cita?.inicio]);

  const cargar = useCallback(async () => {
    if (!cita || !dia) return;
    setCargando(true);
    setElegida(null);
    try {
      const f = dia.format('YYYY-MM-DD');
      const r = await Agenda.disponibilidad({ servicio: cita.servicio, desde: f, hasta: f });
      setHoras(r.dias?.[0]?.horas || []);
    } catch (err) {
      setHoras([]);
      notify.error(mensajeDeError(err, 'No se pudieron cargar las horas'));
    } finally {
      setCargando(false);
    }
  }, [cita, dia, notify]);

  useEffect(() => { if (open) cargar(); }, [open, cargar]);

  const confirmar = async () => {
    setGuardando(true);
    try {
      await Agenda.reprogramar(cita._id, { agendaId: elegida.agendaId, inicio: elegida.inicio });
      notify.success('Hora actualizada. La persona recibe un correo con el cambio.');
      onListo?.();
    } catch (err) {
      notify.error(mensajeDeError(err, 'No se pudo cambiar la hora'));
      cargar();
    } finally {
      setGuardando(false);
    }
  };

  if (!cita) return null;
  const agendar = cita.estado === 'pagada_sin_hora';
  const esHoy = dia.isSame(dayjs(), 'day');

  return (
    <Modal
      open={open} onCancel={onClose} width={640} destroyOnHidden
      title={agendar ? <>Elegir <span className="hl-amarillo">hora</span></> : <>Cambiar <span className="hl-amarillo">hora</span></>}
      footer={
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className={claseBtnSecundario}>Volver</button>
          <button type="button" onClick={confirmar} disabled={!elegida || guardando} className={claseBtnPrimario}>
            {guardando && <LoadingOutlined />} Confirmar hora
          </button>
        </div>
      }
    >
      <p className="text-sm text-gray-600 mb-4">
        {cita.servicioNombre} · <strong>{cita.cliente?.nombre}</strong>
        {cita.inicio && <> · hora actual <strong>{fechaHora(cita.inicio)}</strong> con {cita.agendaNombre}</>}
      </p>

      <div className="flex items-center gap-2 mb-4">
        <button type="button" aria-label="Día anterior" disabled={esHoy} onClick={() => setDia(d => d.subtract(1, 'day'))} className={claseFlecha}>
          <FiChevronLeft size={17} />
        </button>
        <DatePicker value={dia} onChange={(d) => d && setDia(d)} allowClear={false} format="dddd DD/MM/YYYY"
          disabledDate={(d) => d.isBefore(dayjs(), 'day')} className="!rounded-full" style={{ width: 220 }} />
        <button type="button" aria-label="Día siguiente" onClick={() => setDia(d => d.add(1, 'day'))} className={claseFlecha}>
          <FiChevronRight size={17} />
        </button>
      </div>

      {cargando ? <div className="py-10 text-center"><Spin /></div> : horas.length === 0 ? (
        <div className="rounded-2xl bg-gray-50"><Vacio titulo="No hay horas libres ese día" texto="Prueba con el día siguiente." /></div>
      ) : (
        <div className="flex flex-wrap gap-2">
          {horas.flatMap(h => h.agendas.map(a => {
            const activa = elegida?.inicio === h.inicio && elegida?.agendaId === a.id;
            return (
              <button
                type="button" key={`${h.inicio}-${a.id}`} aria-pressed={activa}
                onClick={() => setElegida({ inicio: h.inicio, hora: h.hora, agendaId: a.id, agendaNombre: a.nombre })}
                className={`px-3.5 py-2 rounded-full border text-sm transition-colors text-left
                  ${activa ? 'bg-primary border-primary !text-white' : 'bg-white border-gray-200 text-gray-700 hover:border-primary hover:text-primary'}`}
              >
                <span className="font-bold tabular-nums">{h.hora}</span>
                <span className={`ml-1.5 text-xs ${activa ? 'text-blue-100' : 'text-gray-500'}`}>{a.nombre}</span>
              </button>
            );
          }))}
        </div>
      )}

      {elegida && (
        <Alert type="info" showIcon className="mt-4"
          message={<span className="capitalize">Nueva hora: {fechaLarga(elegida.inicio)}, {elegida.hora} con {elegida.agendaNombre}</span>} />
      )}
      {!agendar && (
        <p className="text-xs text-gray-500 mt-3 mb-0">Los cambios hechos desde el panel no descuentan el cambio de hora que la persona tiene permitido.</p>
      )}
    </Modal>
  );
}
